import React, { useCallback, useEffect, useRef } from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';

import { Button } from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';

import { cleanPortalSessionData } from '../../utils';
import { MAIN_CONTENT_ID } from '../../../../../../../constants';
import { usePaymentPlanStatus } from '../../../../../../../hooks';

const { GET_SESSION_CURRENT_USER } = queryStrings;

const GET_CHARGEBEE_PORTAL_SESSION = gql`
    query getChargebeePortalSession($userId: String) {
        getChargebeePortalSession(user_id: $userId) {
            id
            token
            access_url
            status
            created_at
            expires_at
            object
            customer_id
        }
    }
`;

interface PortalSessionButtonProps {
    chargebeeInstance: ChargebeeInstance;
    label: string;
    /**
     * Callback invoked when the button is visible to the user. This is implemented with
     * IntersectionObserver.
     */
    onButtonViewed(): void;
    /**
     * Callback invoked when the Chargebee subscription is changed
     */
    onSubscriptionChanged(): void;
}

/**
 * A button that uses getChargebeePortalSession to open the Chargebee modal.
 * @param props - {@link HostedPageButtonProps}
 * @returns JSX
 */
export const PortalSessionButton = ({
    chargebeeInstance,
    label,
    onButtonViewed,
    onSubscriptionChanged,
}: PortalSessionButtonProps): JSX.Element | null => {
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id ?? null;

    const [getChargebeePortalSession] = useLazyQuery<{
        getChargebeePortalSession: ChargebeePortalSession;
    }>(GET_CHARGEBEE_PORTAL_SESSION, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            const portalSession = data?.getChargebeePortalSession ?? null;

            const Chargebee: ChargebeeJS | null =
                // eslint-disable-next-line -- the global Chargebee object is loaded in packages/web/public/index.html
                // @ts-ignore
                window?.Chargebee ?? window?.parent?.window?.Chargebee ?? null;

            if (Chargebee && portalSession && chargebeeInstance && userId) {
                chargebeeInstance.setPortalSession(
                    () =>
                        new Promise(resolve => {
                            const cleanPortalSession =
                                cleanPortalSessionData(portalSession);

                            resolve(cleanPortalSession);
                        }),
                );

                const chargebeePortal =
                    chargebeeInstance.createChargebeePortal();

                chargebeePortal.open({
                    close: onSubscriptionChanged,
                });
            }
        },
    });

    const targetRef = useRef<HTMLDivElement | null>(null);

    const observerCallback: IntersectionObserverCallback = useCallback(
        (entries): void => {
            entries.forEach(
                ({ isIntersecting }: IntersectionObserverEntry): void => {
                    if (isIntersecting) {
                        onButtonViewed();
                    }
                },
            );
        },
        [],
    );

    const observerRef = useRef<IntersectionObserver | null>(
        new IntersectionObserver(observerCallback, {
            root: document.getElementById(MAIN_CONTENT_ID),
            rootMargin: '0px',
            threshold: 1,
        }),
    );

    useEffect(() => {
        if (observerRef.current && targetRef.current) {
            observerRef.current.observe(targetRef.current);
        }

        return (): void => {
            if (observerRef.current !== null) {
                observerRef.current.disconnect();
            }
        };
    }, [observerRef.current, targetRef.current]);

    const { subscriptionId } = usePaymentPlanStatus();

    if (subscriptionId === 'override') {
        return null;
    }

    return (
        <Button
            autoWidth={true}
            color="secondary-light-bg"
            handleClick={(): void => {
                if (userId) {
                    getChargebeePortalSession({
                        variables: {
                            user_id: userId,
                        },
                    });
                }
            }}
        >
            <div
                ref={targetRef}
                className="text-sm font-medium tracking-wide text-center px-10"
            >
                {label}
            </div>
        </Button>
    );
};
